import React from 'react';
import Axios from 'axios';
import dotProp from 'dot-prop-immutable';
import qs from 'qs';
import debounce from 'debounce';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

/* Config */
import { reactToastifyConfig } from 'js/config';
import { SWIPER_CONFIG } from 'js/config/swiper';

/* Symbols */
import filterIcon from 'symbols/filter.svg';
import arrowIcon from 'symbols/arrow.svg';

/* Constants */
import { MATCH_MEDIA } from 'js/core/constants';

/* Components */
import VehicleOverviewActiveFilters from './VehicleOverviewActiveFilters';
import VehicleOverviewFilter from './VehicleOverviewFilter';
import VehicleOverviewHeader from './VehicleOverviewHeader';
import VehicleOverviewPagination from './VehicleOverviewPagination';
import VehicleOverviewResults from './VehicleOverviewResults';
import VehicleOverviewSearch from './VehicleOverviewSearch';
import getActiveFilters from './VehicleOverviewDatalayerActiveFilters';

/* Organisms */
import { Modal } from 'js/components/organisms';

/* Atoms */
import { Button } from 'js/components/atoms';
import { Icon } from "js/components/atoms";

/* Context */
import { VehicleOverviewContext } from 'js/context';

/* Utilities */
import { trans, getCloudImageUrl, pushDatalayer } from 'js/utilities';

/* Swiper */
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

export default class VehicleOverview extends React.Component {
    constructor(props) {
        super(props);

        this.overviewRef = React.createRef();
        this.headerRef = React.createRef();
        this.desktopMatchMedia = MATCH_MEDIA.xlup;
        this.urlParams = qs.parse(window.location.search, { plainObjects: false, ignoreQueryPrefix: true });
        this.cancelTokenSource = null;

        this.state = {
            vehicles: [],
            filters: [],
            sorting: [],
            totalResults: 0,
            page: this.urlParams?.page || 1,
            totalPages: 0,
            query: this.urlParams?.query || '',
            isLoading: true,
            isDesktop: this.desktopMatchMedia.matches,
            vehicleType: null,
            scroll: false,
            modalOpen: false,
            partnersModalOpen: false,
            partnersAccentColor: this.props?.derks_partners?.accent_color,
            hasConversionVehicleList: !!this.props.conversionUpdateUrl,
            useConversionVehicleList: false,
            conversionRuleType: '',
            conversionForms: this.props.derks_conversion_forms
        };

        toast.configure(reactToastifyConfig);
        this.desktopMatchMedia.addEventListener('change', this.mediaListener);
    }

    componentDidMount = () => {
        const useConversion = this.urlParams && this.urlParams.ombouw === '1';
        if (useConversion) this.setState({ useConversionVehicleList: true })

        if (this.urlParams && this.urlParams.sold === '1') {
            toast.info(trans('sulu.content_block.vehicle_overview.vehicle.sold'));
        }

        this.fetchData(
            false,
            {
                ...(this.props.sort ? { sort: this.props.sort } : null),
                partnervoertuig: [0, 1],
                ...(Object.keys(this.urlParams).length === 0 ? {
                    ...(this.props.carrosserie ? { carrosserie: [this.props.carrosserie] } : null),
                    ...(this.props.brand ? { brand: [this.props.brand] } : null),
                    ...((this.props.brand && this.props.model) ? { [`model[${ this.props.brand }]`]: [this.props.model] } : null),
                } : {}),
                ...this.urlParams,
            },
            false, true, useConversion

        );

        this.observeHeader();
    }

    componentWillUnmount = () => {
        this.desktopMatchMedia.removeEventListener('change', this.mediaListener);
    }

    mediaListener = (event) => {
        this.setState({ isDesktop: event.matches });
    };

    observeHeader = () => {
        const headerObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                // Check if header is above viewport
                if (entry.boundingClientRect.bottom < 0) {
                    this.setState({ scroll: true });
                } else {
                    this.setState({ scroll: false });
                }
            });
        });

        headerObserver.observe(this.headerRef.current);
    }

    fetchData = (saveParams = true, params, scroll = false, initialFetch = false, useConversion = false) => {
        this.setState({ isLoading: true, });

        if (saveParams) this.replaceHistoryState(params);

        this.cancelTokenSource?.cancel();
        this.cancelTokenSource = Axios.CancelToken.source();

        Axios.get(
            this.state.useConversionVehicleList || useConversion ? this.props.conversionUpdateUrl : this.props.updateUrl,
            {
                params: params,
                cancelToken: this.cancelTokenSource.token,
                paramsSerializer: (params) => {
                    return qs.stringify(
                        params,
                        {
                            arrayFormat: 'brackets',
                            encodeValuesOnly: true,
                        },
                    );
                },
            },
        ).then((response) => {
            if (response.data) {
                const { items, filters, sorting, count, pages, content } = response.data;
                let conversionRuleType = ''

                if (content.rules) {
                    if (content.rules.length > 0) {
                        const conversionTypes = ['enriched_mourning_surcharge_set', 'enriched_transfer_surcharge_set', 'enriched_cooling_surcharge_set', 'enriched_multi_temp_surcharge_set', 'enriched_mourning_or_transfer_surcharge_set', 'enriched_cooling_or_multi_temp_surcharge_set'];
                        const activeConversionType = content.rules.find(rule => conversionTypes.includes(rule.documentField));

                        if (activeConversionType) {
                            conversionRuleType = activeConversionType.documentField;
                        }
                    }
                }

                this.setState({
                    vehicles: items,
                    filters: filters?.items,
                    sorting: sorting?.items,
                    totalResults: count,
                    page: pages.current,
                    totalPages: pages.total,
                    conversionRuleType: conversionRuleType
                });

                if (initialFetch) {
                    const datalayerData = {
                        event: 'view_item_list_leadgen',
                        items: items.map((item, itemIndex) => ({
                            index: `${ itemIndex + 1 }`,
                            item_id: `${ item?.id || '' }`,
                            item_id2: `${ item?.attributes?.voertuignr_hexon?.value || '' }`.toLowerCase(),
                            item_awd: `${ item?.attributes?.voertuignr?.value || '' }`,
                            item_brand: `${ item?.brand || '' }`.toLowerCase(),
                            item_model: `${ item?.model || '' }`.toLowerCase(),
                            item_license_plate: `${ item?.attributes?.kenteken?.value || '' }`,
                            item_body: `${ item?.attributes?.carrosserie?.value || '' }`.toLowerCase(),
                            item_online_date: format(new Date(item?.createdAt), 'yyyy-MM-dd HH:mm:ss'),
                            item_transmission: `${ item?.attributes?.transmissie?.value || '' }`,
                            item_condition: `${ item?.attributes?.source?.value || '' }`.toLowerCase(),
                            item_fuel_type: `${ item?.attributes?.brandstof?.value || '' }`.toLowerCase(),
                            item_stock_type: `${ item?.attributes?.source?.value || '' }`.toLowerCase(),
                            item_odemeter_distance: `${ item?.attributes?.tellerstand?.value || '' }`,
                            item_color: `${ item?.attributes?.kleur?.value || '' }`.toLowerCase(),
                            item_construction_year: `${ item?.attributes?.bouwjaar?.value || '' }`,
                            item_type: `${ item?.type || '' }`.toLowerCase(),
                            item_category: `${ item?.attributes?.thema?.value || '' }`,
                            catalog_price: `${ item?.price || '' }`,
                            sale_price: `${ item?.attributes?.actieprijs?.value || '' }`,
                            financial_lease_price: `${ item?.enrichedValues?.finance_price || '' }`,
                            operational_lease_price: `${ item?.enrichedValues?.lease_price || '' }`,
                            price_category: null, // hebben we niet,
                            affiliation: `${ item?.attributes?.vestiging?.value || '' }`.toLowerCase(),
                            item_list_name: this.props?.vehicle_list.name || '',
                            item_list_id: this.props?.vehicle_list.id.toString() || '',
                            currency: 'EUR',
                        }))
                    };

                    pushDatalayer(datalayerData);
                }
            }
        }).catch((error) => {
            if (!Axios.isCancel(error)) {
                throw new Error(error);
            }
        }).finally(() => {
            this.setState({ isLoading: false });

            if (scroll) {
                this.overviewRef.current.scrollIntoView();
            }
        });
    };

    getFilterParams = (excludeRangeFilters = false) => {
        const filterParams = {};

        this.state.filters?.forEach(filter => {
            filter?.filters?.forEach(filter => {
                if (excludeRangeFilters && ['open_input_range_list', 'range_list'].includes(filter.viewType)) {
                    return;
                }
                if (excludeRangeFilters && filter.name === 'Carrosserie') {
                    return;
                }
                this.addFilterToParams(filter, filterParams);
            });
        });

        return filterParams;
    }

    getSortParam = () => {
        const sort = this.state.sorting.find(sort => sort?.active) || null;

        return sort ? { sort: sort.key } : {};
    }

    getSearchParam = () => {
        return this.state.query ? { query: this.state.query } : {};
    }

    defaultFilterParams = () => {
        return {
            partnervoertuig: [0, 1]
        }
    }

    addFilterToParams = (filter, params) => {
        if (['select', 'multi_select'].includes(filter?.viewType)) {
            const activeChoices = filter?.choices?.filter(choice => choice?.active);

            if (activeChoices?.length) {
                Object.assign(params, { [filter?.filterKey]: [...activeChoices?.map(choice => choice?.key), ...(params?.[filter?.filterKey] || [])] });

                activeChoices.forEach(choice => {
                    choice.filters.forEach(filter => {
                        this.addFilterToParams(filter, params);
                    });
                });
            }
        }

        if (['open_input_range_list', 'range_list'].includes(filter?.viewType)) {
            Object.assign(params, { [filter?.filterKey]: `${ filter?.selectedMinValue || '' };${ filter?.selectedMaxValue || '' }` });
        }

        return params;
    }

    replaceHistoryState = (params = {}) => {
        const queryString = qs.stringify(params, { arrayFormat: 'brackets' });
        history.replaceState({}, document.title, `${ location.pathname }${ queryString && `?${ queryString }` }`);
    };

    updatePage = (page) => {
        this.fetchData(
            true,
            {
                ...this.getFilterParams(),
                ...this.getSortParam(),
                ...this.getSearchParam(),
                page,
            },
            true
        );
    };

    updateState = (stateKey, path = null, value, doRequest = false, getConversionVehicles = false, cleanParams = false) => {

        let newState = value;
        if (typeof newState !== 'function') {
            newState = () => value;
        }

        this.setState(
            (state) => ({
                [stateKey]: path ? dotProp.set(state[stateKey], path, newState) : newState(state[stateKey]),
            }),
            () => {
                if (!doRequest) return;

                this.debounceFetchData(true, {
                    ...(getConversionVehicles ? this.getFilterParams(true) : this.getFilterParams()),
                    ...this.getSortParam(),
                    ...this.getSearchParam(),
                    ...(getConversionVehicles ? { partnervoertuig: [0, 1] } : null),
                    ...(this.state.useConversionVehicleList ? { ombouw: 1 } : null),
                    ...(cleanParams ? { brand: [] } : null),
                    page: 1,
                });
                this.cancelTokenSource?.cancel();
            }
        );
    }

    resetFilters = () => {

        this.setState({ useConversionVehicleList: false });

        this.debounceFetchData(true,
            {
                ...this.getSortParam(),
                ...(this.props.derks_partners_toggle ? this.defaultFilterParams() : null)
            }
        )
    }

    debounceFetchData = debounce(this.fetchData, 500);

    render() {
        const purchase_type = this.state.filters.find((filter) => filter.key === 'aanschafvorm')?.filters?.[0]?.choices?.find((choice) => choice.active)?.name || null;

        return (
            <VehicleOverviewContext.Provider
                value={{
                    fetchData: this.fetchData,
                    updateState: this.updateState,
                    updateFilters: (path, value) => this.updateState('filters', path, value, true),
                    updateSorting: (path, value) => this.updateState('sorting', path, value, true),
                    updateQuery: (value) => this.updateState('query', null, value, true),
                    resetFilters: this.resetFilters,
                    partnersModalOpen: (value) => this.setState({ partnersModalOpen: value }),
                    partnersAccentColor: this.state.partnersAccentColor,
                    hasConversionVehicleList: this.state.hasConversionVehicleList,
                    useConversionVehicleList: this.state.useConversionVehicleList,
                    conversionRuleType: this.state.conversionRuleType,
                    conversionForms: this.state.conversionForms,
                    conversionFormModalOpen: (conversion_type) => {
                        const updatedConversionForms = this.state.conversionForms.map((conversionForm) => {
                            if (conversionForm.conversion_type === conversion_type) {
                                return { ...conversionForm, modal_open: true }
                            }
                            return conversionForm
                        })
                        this.setState({ conversionForms: updatedConversionForms })
                    }
                }}
            >
                <div ref={this.overviewRef} className="row gutters-2 align-items-baseline mb-6 mb-xl-0">
                    {this.state.isDesktop && (
                        <div className="col-12 col-xl-4 col-xxl-3">
                            <div className="pr-10">
                                <p className="h6 mb-4 text-primary">
                                    {trans('sulu.content_block.vehicle_overview.filter.title')}
                                </p>
                                <VehicleOverviewSearch
                                    query={this.state.query}
                                />
                                <hr className="my-4" />
                                <VehicleOverviewFilter
                                    filters={this.state.filters}
                                    useConversionVehicleList={this.state.useConversionVehicleList}
                                />
                            </div>
                        </div>
                    )}
                    <div className="col-12 col-xl-8 col-xxl-9">
                        <div className="mb-4 mb-xl-6">
                            <VehicleOverviewHeader
                                headerRef={this.headerRef}
                                filters={this.state.filters}
                                sorting={this.state.sorting}
                                query={this.state.query}
                                totalResults={this.state.totalResults}
                                isDesktop={this.state.isDesktop}
                                isLoading={this.state.isLoading}
                                modalOpen={this.state.modalOpen}
                                setModalOpen={(value) => this.setState({ modalOpen: value })}
                                derksPartners={this.props.derks_partners}
                                derksPartnersToggle={this.props.derks_partners_toggle}
                                hasConversionVehicleList={this.state.hasConversionVehicleList}
                            />
                        </div>
                        <div className="mb-6">
                            <VehicleOverviewActiveFilters
                                filters={this.state.filters}
                            />
                        </div>
                        <VehicleOverviewResults
                            specs={this.props.specs}
                            vehicles={this.state.vehicles}
                            isLoading={this.state.isLoading}
                            banners={this.props.banners}
                            purchase_type={purchase_type}
                            locale={this.props.locale}
                            vehicleList={this.props.vehicle_list}
                            datalayerActiveFilters={getActiveFilters(this.state.filters)}
                        />
                        <VehicleOverviewPagination
                            page={this.state.page}
                            totalPages={this.state.totalPages}
                            updatePage={this.updatePage}
                            isDesktop={this.state.isDesktop}
                        />
                    </div>
                </div>
                <div className={`vehicle-overview__sticky-button ${ this.state.scroll ? 'is-active' : '' } d-xl-none`}>
                    <Button
                        text={trans('sulu.content_block.vehicle_overview.filter.title')}
                        theme="primary"
                        className="btn-block"
                        icon={{ url: filterIcon }}
                        onClick={() => this.setState({ modalOpen: true })}
                    />
                </div>
                {this.props.derks_partners ?
                    <Modal
                        open={this.state.partnersModalOpen}
                        title={this.props.derks_partners?.top_title}
                        handleClose={() => this.setState({ partnersModalOpen: false })}
                        bodyTitle={this.props.derks_partners?.header_title}
                        theme={this.props.derks_partners?.accent_color}
                    >
                        {this.props.derks_partners?.text ?
                            <div dangerouslySetInnerHTML={{ __html: this.props.derks_partners?.text }}></div>
                            : null}
                    </Modal>
                    : null}
                {this.state.conversionForms.length > 0 ?
                    this.state.conversionForms.map((conversionForm, index) => (
                        <Modal
                            key={index}
                            open={conversionForm?.modal_open}
                            title={conversionForm?.top_title}
                            handleClose={() => {
                                const updatedConversionForms = this.state.conversionForms.map((stateConversionForm) => {
                                    if (stateConversionForm.conversion_type === conversionForm.conversion_type) {
                                        return { ...stateConversionForm, modal_open: false }
                                    }
                                    return stateConversionForm
                                })
                                this.setState({ conversionForms: updatedConversionForms })
                            }}
                            bodyTitle={conversionForm?.header_title}
                            theme={conversionForm?.accent_color}
                        >
                            {conversionForm?.images.length > 0 ?
                                <div className="mb-8 position-relative">
                                    <Swiper
                                        id={conversionForm.conversion_type}
                                        {...SWIPER_CONFIG}
                                        modules={[Navigation, Pagination]}
                                        navigation={{
                                            prevEl: '.swiper-button-prev',
                                            nextEl: '.swiper-button-next',
                                        }}
                                        pagination={{ clickable: true, }}
                                        spaceBetween={0}
                                        slidesPerView={1}
                                        loop
                                    >
                                        {conversionForm?.images?.map((image, index) => (
                                            <SwiperSlide key={index}>
                                                <img
                                                    {...{
                                                        src: getCloudImageUrl(image, { w: 650 }),
                                                    }}
                                                    className="img-fluid rounded"
                                                />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                    <div id={`${ conversionForm.conversion_type }-prev`} className="swiper-button-prev">
                                        <Icon url={arrowIcon} size={24} />
                                    </div>
                                    <div id={`${ conversionForm.conversion_type }-prev`} className="swiper-button-next">
                                        <Icon url={arrowIcon} size={24} />
                                    </div>
                                </div>
                                : null}
                            {conversionForm?.text ?
                                <div dangerouslySetInnerHTML={{ __html: conversionForm?.text }}></div>
                                : null}
                        </Modal>
                    )
                    )
                    :
                    null
                }
            </VehicleOverviewContext.Provider>
        )
            ;
    }
}
